
import { useState } from 'react';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { name: 'الرئيسية', href: 'hero' },
    { name: 'عن الملتقى', href: 'about' },
    { name: 'الأهداف', href: 'objectives' },
    { name: 'الإطار المنطقي', href: 'logical-framework' },
    { name: 'الخطة التنفيذية', href: 'execution-plan' },
    { name: 'الجدول الزمني', href: 'schedule' },
    { name: 'المحتوى العلمي', href: 'work-content' },
    { name: 'الموازنة', href: 'budget' },
    { name: 'مؤشرات الأداء', href: 'kpis' },
    { name: 'أصحاب المصلحة', href: 'stakeholders' },
    { name: 'إدارة المخاطر', href: 'risk-management' },
    { name: 'المعرض المصاحب', href: 'exhibition' },
    { name: 'فريق العمل', href: 'team' },
    { name: 'الشركاء', href: 'partners' },
    { name: 'التسجيل', href: 'registration' },
    { name: 'اتصل بنا', href: 'contact' },
  ];

  /** Scroll smoothly to a section; if the element does not exist, log a warning. */
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      console.warn(`Navbar: element with id "${id}" not found.`);
      return;
    }
    element.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <div className="fixed top-0 right-0 left-0 z-50 px-4 md:px-8 pt-4 md:pt-6">
      <nav
        className={`max-w-6xl mx-auto rounded-2xl transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-white/10 backdrop-blur-md border border-white/20'
        }`}
      >
        <div className="px-4 md:px-6 py-2.5 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo — يمين الشريط */}
            <div
              className={`text-lg md:text-xl lg:text-2xl font-bold transition-colors ${
                scrolled ? 'text-[#1B5E20]' : 'text-white'
              }`}
            >
              ملتقى احتياجات الشباب
            </div>

            {/* Desktop Navigation — يسار الشريط */}
            <div className="hidden lg:flex items-center gap-6">
              <button
                onClick={() => scrollToSection('hero')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                الرئيسية
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                عن الملتقى
              </button>
              <button
                onClick={() => scrollToSection('objectives')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                الأهداف
              </button>
              <button
                onClick={() => scrollToSection('schedule')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                البرنامج
              </button>
              <button
                onClick={() => scrollToSection('work-content')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                المحتوى العلمي
              </button>
              <button
                onClick={() => scrollToSection('registration')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                التسجيل
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-800 hover:text-[#1B5E20]' : 'text-white hover:text-[#D4AF37]'
                }`}
              >
                تواصل معنا
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
                scrolled ? 'text-gray-800' : 'text-white'
              }`}
            >
              <i className={`${isOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div className="lg:hidden mt-4 pb-2 space-y-2">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`block w-full text-right px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-gray-800 hover:bg-[#1B5E20]/10 hover:text-[#1B5E20]'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {link.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>
    </div>
  );
}
